<template>
  <!-- The environment bar belongs to the whole app, not just the signed-in
       shell, so it lives here rather than in AppLayoutView: login is exactly
       where knowing which environment you are about to sign in to matters.
       The column owns the viewport height and AppLayoutView fills what is
       left, which is why that view is h-full and not h-dvh. -->
  <div class="flex h-dvh flex-col">
    <EnvBar />
    <div class="relative min-h-0 flex-1 overflow-auto">
      <router-view />
      <div
        v-if="!currentUser && ready && $route.name !== 'PublicDisplay'"
        class="absolute top-2 right-3">
        <LangSelect />
      </div>
    </div>
  </div>
  <ModalHost />
  <GlobalMenuHost />
  <ActionSearchBar v-if="currentUser" />
  <!-- @click.stop prevents toast clicks from reaching the window-level click listener in SidePane -->
  <Teleport to="body">
    <div @click.stop>
      <Toaster
        position="bottom-left"
        :visibleToasts="5"
        :duration="5000"
        closeButton
        closeButtonPosition="top-right"
        expand />
    </div>
  </Teleport>
</template>

<script lang="ts">
import { Toaster } from 'vue-sonner'
import { i18n } from '@/locales'
import { useAccountStore } from '@/stores/accountStore'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useActionSearchStore } from '@/stores/actionSearchStore'
import type { TLabManagementLocale } from '@team-milva/lab-models/language'
import { mapState, mapActions } from 'pinia'
import { hasUnsavedChanges } from '@/composables/useUnsavedChangesGuard'

export default {
  name: 'App',
  components: { Toaster },
  computed: {
    ...mapState(useUserStore, ['currentUser', 'isLabAdminUser']),
    ...mapState(useAuthStore, ['ready']),
    ...mapState(useAccountStore, ['availableLanguages']),
  },
  watch: {
    availableLanguages(
      newLangs: ReturnType<typeof useAccountStore>['availableLanguages']
    ) {
      // Admins keep their own language regardless of the visited lab's
      // labLanguages — no fallback, no reset (see userStore.getCurrentUser).
      if (this.isLabAdminUser) return

      const supportedLocales = newLangs.map(
        (l) => l.locale
      ) as TLabManagementLocale[]

      const currentLocale = i18n.global.locale.value as TLabManagementLocale
      if (!supportedLocales.includes(currentLocale)) {
        i18n.global.locale.value = 'en'
        localStorage.setItem('lang', 'en')
      }
    },
  },
  methods: {
    ...mapActions(useActionSearchStore, ['toggle']),
    onKeydown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (this.currentUser) this.toggle()
      }
    },
    // The tab-close / reload half of the unsaved-changes guard. In-app
    // navigation is handled by the router guard in router/index.ts; this
    // covers the one departure the router never sees. The browser shows its
    // own generic dialog here — it cannot be styled, and the modern API only
    // reads preventDefault(), not a custom message.
    //
    // Registered unconditionally, including dev and e2e. Gating it behind
    // import.meta.env.DEV would leave the hardest-to-test part of the guard
    // as the part nobody on the team ever exercises.
    onBeforeUnload(e: BeforeUnloadEvent) {
      if (!hasUnsavedChanges()) return
      e.preventDefault()
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown as EventListener)
    window.removeEventListener('beforeunload', this.onBeforeUnload as EventListener)
  },
}
</script>

